import { useRef } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
} from '@/ui';

type Props = {
  isOpen: boolean;
  calendarName: string;
  /** Set only while this calendar's own unsubscribe is in flight. */
  pendingChoice: 'keep' | 'remove' | null;
  onClose: () => void;
  /** removeEvents: also delete the events the subscription pushed. */
  onUnsubscribe: (removeEvents: boolean) => void;
};

// Adapted from #2988's dialog of the same name. Unsubscribing always stops the
// sync; the choice is what happens to the events already pushed. The spinner
// tracks which answer was given, so keeping events never lights up the
// destructive button.
export const UnsubscribeDialog = ({
  isOpen,
  calendarName,
  pendingChoice,
  onClose,
  onUnsubscribe,
}: Props) => {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Escape and overlay clicks close a Chakra dialog by default. While an
  // unsubscribe is in flight that would strand the spinner and let a second
  // row start one over the top of it, so both are shut off until it settles.
  const isPending = !!pendingChoice;

  return (
    <AlertDialog
      closeOnEsc={!isPending}
      closeOnOverlayClick={!isPending}
      isCentered
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="ah-2xl" mx={4}>
          <AlertDialogHeader fontFamily="display" fontSize="xl" pb={1}>
            Stop syncing this schedule?
          </AlertDialogHeader>
          <AlertDialogBody color="ink.soft" fontSize="ah-row">
            New and updated events will stop arriving in {calendarName}. You can
            keep the events already added, or have them removed (removal
            finishes in the background and can take a few minutes).
          </AlertDialogBody>
          <AlertDialogFooter>
            <Flex gap={2} justify="flex-end" w="100%" wrap="wrap">
              <Button
                isDisabled={isPending}
                ref={cancelRef}
                size="ah"
                variant="ahGhost"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                isDisabled={isPending && pendingChoice !== 'keep'}
                isLoading={pendingChoice === 'keep'}
                size="ah"
                variant="ahSecondary"
                onClick={() => onUnsubscribe(false)}
              >
                Keep events
              </Button>
              <Button
                colorScheme="destructive"
                isDisabled={isPending && pendingChoice !== 'remove'}
                isLoading={pendingChoice === 'remove'}
                size="ah"
                onClick={() => onUnsubscribe(true)}
              >
                Remove events
              </Button>
            </Flex>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};
